import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core'
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms'
import {atLeastOneOptionSelected} from '../../../../core/forms/at-least-one-selected.validator'
import {ActivatedRoute, Router} from '@angular/router'
import {JobRequestRepository} from '../job-request.repository'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {OfferingResolved} from '../resolvers/job-request-offering.resolver'
import {ClientExpectation} from '../../../../../__generated__/types'
import {JobRequestService} from '../job-request.service'
import {Observable} from 'rxjs'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {JobRequestPageMetadataService} from '../job-request-page-metadata.service'

@Component({
  templateUrl: './job-request-client-expectation-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class JobRequestClientExpectationPageComponent extends PageComponent implements OnInit {
  private readonly intentionRoute = PUBLIC_ROUTES.JOB_REQUEST_INTENTION
  private readonly detailRoute = PUBLIC_ROUTES.JOB_REQUEST_DETAIL

  readonly clientExpectationControls = {
    estimation: new UntypedFormControl(false),
    consulting: new UntypedFormControl(false),
    offer: new UntypedFormControl(false),
    execution: new UntypedFormControl(false),
  }

  readonly offering: OfferingResolved = this.route.snapshot.data['offering']
  readonly formGroup = new UntypedFormGroup(this.clientExpectationControls, [atLeastOneOptionSelected()])

  readonly calculateCostIcon = {
    collection: 'fak',
    name: 'calclulate-costs',
  }
  readonly consultingIcon = {
    collection: 'fak',
    name: 'consulting-1',
  }
  readonly getOfferIcon = {
    collection: 'fak',
    name: 'get-offer',
  }
  readonly executionIcon = {
    collection: 'fak',
    name: 'execution',
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly repository: JobRequestRepository,
    private readonly jobRequestService: JobRequestService,
    private readonly jobRequestPageMetadataService: JobRequestPageMetadataService,
  ) {
    super()
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.jobRequestPageMetadataService.getPageMetadataForStep('EXPECTATIONS', this.offering)
  }

  ngOnInit() {
    // If client expectation is not enabled go to detail page
    if (!this.offering?.showClientExpectation) {
      this.router.navigate([this.detailRoute.routingPath], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
      })
      return
    }

    const intentionSlug = this.offering?.areIntentionsEnabled ? this.repository.getIntentionSlug() : undefined

    // If intention is not set but enabled for offering go to intention page
    if (!intentionSlug && this.intentionsEnabled()) {
      this.router.navigate([this.intentionRoute.routingPath], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
      })
      return
    }

    // start request process if not started yet by an intentions page (meaning clear non reusable state)
    if (!intentionSlug) {
      this.jobRequestService.requestProcessDidStart(this.offering?.id)
      // Only store if offering state from same offering is not existent
      if (!this.repository.readJobRequestOfferingState()) {
        this.repository.storeJobRequestOfferingState({
          id: this.offering!.id,
          questions: [],
        })
      }
    }
    this.hydrateFormState(this.repository.readClientExpectationState())
  }

  hydrateFormState(clientExpectations?: ClientExpectation) {
    if (clientExpectations) {
      this.formGroup.patchValue(clientExpectations)
    }
  }

  next() {
    this.formGroup.markAllAsTouched()
    if (this.formGroup.errors) {
      return
    }
    this.repository.storeClientExpectationState(this.formGroup.getRawValue())
    this.router.navigate([this.detailRoute.routingPath], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'merge',
    })
  }

  back() {
    if (this.intentionsEnabled()) {
      this.router.navigate([this.intentionRoute.routingPath], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
      })
    }
  }

  intentionsEnabled() {
    return !!(this.offering?.areIntentionsEnabled && this.offering.intentions?.length)
  }
}
